import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Button } from '../components/ui/button';
import { formatCurrency, formatNumber, getShortMonthName, INDIAN_FY_MONTHS, downloadCSV, CLIENT_TYPES } from '../lib/utils';
import { Loader2, Download, CheckCircle, XCircle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const COLORS = ['#2A9D8F', '#264653', '#E9C46A', '#E76F51'];
const CHART_COLORS = { Client: '#2A9D8F', Partner: '#F97316' }; // Orange for Partner

export default function ReportsPage() {
  const [activeTab, setActiveTab] = useState('monthwise');
  const [loading, setLoading] = useState(false);
  const [financialYears, setFinancialYears] = useState([]);
  const [selectedFY, setSelectedFY] = useState('');
  const [selectedType, setSelectedType] = useState('');

  // Report data states
  const [monthwiseData, setMonthwiseData] = useState([]);
  const [matrixData, setMatrixData] = useState([]);
  const [unpaidByMonth, setUnpaidByMonth] = useState([]);
  const [unpaidByClient, setUnpaidByClient] = useState([]);
  const [currencyData, setCurrencyData] = useState([]);
  const [bankData, setBankData] = useState([]);

  useEffect(() => {
    fetchFinancialYears();
  }, []);

  useEffect(() => {
    if (activeTab) {
      fetchReportData(activeTab);
    }
  }, [activeTab, selectedFY, selectedType]);

  const fetchFinancialYears = async () => {
    try {
      const response = await axios.get(`${API_URL}/dashboard/stats`);
      setFinancialYears(response.data.financial_years || []);
      if (response.data.financial_years?.length > 0) {
        setSelectedFY(response.data.financial_years[0]);
      }
    } catch (error) {
      console.error('Failed to fetch FY:', error);
    }
  };

  const fetchReportData = async (report) => {
    setLoading(true);
    try {
      const params = {};
      if (selectedFY && selectedFY !== 'all') params.financial_year = selectedFY;
      if (selectedType && selectedType !== 'all') params.client_type = selectedType;

      switch (report) {
        case 'monthwise':
          const monthRes = await axios.get(`${API_URL}/reports/monthwise-revenue`, { params });
          setMonthwiseData(monthRes.data.map(item => ({
            month: getShortMonthName(item.month),
            monthNum: item.month,
            Client: item.Client || 0,
            Partner: item.Partner || 0,
            Total: (item.Client || 0) + (item.Partner || 0)
          })));
          break;

        case 'matrix':
          const matrixRes = await axios.get(`${API_URL}/reports/client-month-matrix`, { 
            params: { ...params, active_only: true }
          });
          setMatrixData(matrixRes.data);
          break;

        case 'unpaid-month':
          const unpaidMonthRes = await axios.get(`${API_URL}/reports/unpaid-by-month`);
          setUnpaidByMonth(unpaidMonthRes.data);
          break;

        case 'unpaid-client':
          const unpaidClientRes = await axios.get(`${API_URL}/reports/unpaid-by-client`);
          setUnpaidByClient(unpaidClientRes.data);
          break;

        case 'currency':
          const currRes = await axios.get(`${API_URL}/reports/currency-revenue`, { params });
          setCurrencyData(currRes.data);
          break;

        case 'bank':
          const bankRes = await axios.get(`${API_URL}/reports/bank-inflow`, { params });
          setBankData(bankRes.data);
          break;

        default:
          break;
      }
    } catch (error) {
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const exportReport = (data, filename) => {
    if (!data || data.length === 0) {
      toast.error('No data to export');
      return;
    }
    downloadCSV(data, filename);
    toast.success('Report exported successfully');
  };

  const renderFilters = () => (
    <div className="flex flex-wrap items-center gap-4 mb-6">
      <Select value={selectedFY || "all"} onValueChange={setSelectedFY}>
        <SelectTrigger className="w-48" data-testid="report-fy-filter">
          <SelectValue placeholder="Financial Year" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Years</SelectItem>
          {financialYears.map(fy => (
            <SelectItem key={fy} value={fy}>{fy}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {!['unpaid-month', 'unpaid-client'].includes(activeTab) && (
        <Select value={selectedType || "all"} onValueChange={setSelectedType}>
          <SelectTrigger className="w-40" data-testid="report-type-filter">
            <SelectValue placeholder="Client Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {CLIENT_TYPES.map(t => (
              <SelectItem key={t} value={t}>{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Reports</h1>
        <p className="text-muted-foreground">Financial analytics and insights</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-muted p-1 rounded-lg flex-wrap h-auto">
          <TabsTrigger value="monthwise" data-testid="tab-monthwise">Monthwise Revenue</TabsTrigger>
          <TabsTrigger value="matrix" data-testid="tab-matrix">Client vs Month</TabsTrigger>
          <TabsTrigger value="unpaid-month" data-testid="tab-unpaid-month">Unpaid by Month</TabsTrigger>
          <TabsTrigger value="unpaid-client" data-testid="tab-unpaid-client">Unpaid by Client</TabsTrigger>
          <TabsTrigger value="currency" data-testid="tab-currency">Currency Revenue</TabsTrigger>
          <TabsTrigger value="bank" data-testid="tab-bank">Bank Inflow</TabsTrigger>
        </TabsList>

        {/* Report 1: Monthwise Revenue */}
        <TabsContent value="monthwise">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Monthwise Revenue (Client vs Partner)</CardTitle>
              <Button variant="outline" size="sm" onClick={() => exportReport(monthwiseData, 'monthwise_revenue')}>
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </CardHeader>
            <CardContent>
              {renderFilters()}
              {loading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : monthwiseData.length > 0 ? (
                <div className="h-96">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={monthwiseData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="month" tick={{ fill: '#6B7280', fontSize: 12 }} />
                      <YAxis tick={{ fill: '#6B7280', fontSize: 12 }} tickFormatter={formatNumber} />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                      <Bar dataKey="Client" stackId="a" fill={CHART_COLORS.Client} />
                      <Bar dataKey="Partner" stackId="a" fill={CHART_COLORS.Partner} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">No data available</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Report 2: Client vs Month Matrix */}
        <TabsContent value="matrix">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Client vs Month Payment Matrix</CardTitle>
              <Button variant="outline" size="sm" onClick={() => exportReport(matrixData, 'client_month_matrix')}>
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </CardHeader>
            <CardContent>
              {renderFilters()}
              {loading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : matrixData.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-primary/5">
                        <TableHead className="sticky left-0 bg-white z-10">Client Name</TableHead>
                        {INDIAN_FY_MONTHS.map(m => (
                          <TableHead key={m} className="text-center min-w-20">
                            {getShortMonthName(m)}
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {matrixData.map((row) => (
                        <TableRow key={row.client_id}>
                          <TableCell className="font-medium sticky left-0 bg-white z-10">
                            {row.client_name}
                          </TableCell>
                          {INDIAN_FY_MONTHS.map(m => {
                            const value = row[`month_${m}`];
                            const hasPaid = value > 0;
                            return (
                              <TableCell 
                                key={m} 
                                className={`text-center ${hasPaid ? 'bg-green-50' : 'bg-red-50'}`}
                              >
                                {hasPaid ? (
                                  <span className="font-mono text-green-700 text-sm">
                                    {formatNumber(value)}
                                  </span>
                                ) : (
                                  <XCircle className="h-4 w-4 text-red-400 mx-auto" />
                                )}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">No data available</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Report 3: Unpaid by Month */}
        <TabsContent value="unpaid-month">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Unpaid Invoices by Billing Month</CardTitle>
              <Button variant="outline" size="sm" onClick={() => {
                const flatData = unpaidByMonth.flatMap(g => g.invoices.map(inv => ({
                  billing_month: g.billing_month,
                  billing_year: g.billing_year,
                  ...inv
                })));
                exportReport(flatData, 'unpaid_by_month');
              }}>
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : unpaidByMonth.length > 0 ? (
                <div className="space-y-4">
                  {unpaidByMonth.map((group, idx) => (
                    <Card key={`${group.billing_month}-${group.billing_year || idx}`} className="border-l-4 border-l-amber-500">
                      <CardHeader className="py-3">
                        <CardTitle className="text-base flex items-center justify-between">
                          <span>
                            {group.billing_month}
                            {group.billing_year && <span className="text-muted-foreground ml-1">({group.billing_year})</span>}
                          </span>
                          <div className="text-sm font-normal text-muted-foreground">
                            {group.count} invoice(s) • Est. {formatCurrency(group.total_estimated_inr || 0)}
                          </div>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="py-0 pb-4">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Invoice No</TableHead>
                              <TableHead>Client</TableHead>
                              <TableHead>Billing Month</TableHead>
                              <TableHead className="text-right">Amount</TableHead>
                              <TableHead>Currency</TableHead>
                              <TableHead className="text-right">Exchange Rate</TableHead>
                              <TableHead className="text-right">Est. INR</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {group.invoices.map((inv) => (
                              <TableRow key={inv.invoice_no}>
                                <TableCell className="font-mono">{inv.invoice_no}</TableCell>
                                <TableCell>{inv.client_name}</TableCell>
                                <TableCell>
                                  {inv.billing_month}
                                  {inv.billing_year && <span className="text-muted-foreground text-xs ml-1">({inv.billing_year})</span>}
                                </TableCell>
                                <TableCell className="text-right font-mono">
                                  {formatCurrency(inv.bill_amount, inv.bill_currency)}
                                </TableCell>
                                <TableCell>{inv.bill_currency}</TableCell>
                                <TableCell className="text-right font-mono text-muted-foreground">
                                  {inv.exchange_rate ? inv.exchange_rate.toFixed(2) : '-'}
                                </TableCell>
                                <TableCell className="text-right font-mono text-primary font-semibold">
                                  {formatCurrency(inv.estimated_revenue_inr || inv.bill_amount)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-4" />
                  No unpaid invoices
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Report 4: Unpaid by Client */}
        <TabsContent value="unpaid-client">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Unpaid Invoices by Client</CardTitle>
              <Button variant="outline" size="sm" onClick={() => {
                const flatData = unpaidByClient.flatMap(g => g.invoices.map(inv => ({
                  client_name: g.client_name,
                  ...inv
                })));
                exportReport(flatData, 'unpaid_by_client');
              }}>
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : unpaidByClient.length > 0 ? (
                <div className="space-y-4">
                  {unpaidByClient.map((group) => (
                    <Card key={group.client_id} className="border-l-4 border-l-red-500">
                      <CardHeader className="py-3">
                        <CardTitle className="text-base flex items-center justify-between">
                          <span className="text-lg font-semibold">{group.client_name}</span>
                          <div className="text-sm font-normal">
                            <span className="text-muted-foreground">{group.count} invoice(s)</span>
                            <span className="mx-2">•</span>
                            <span className="font-mono text-destructive">{formatCurrency(group.total_estimated_inr || group.total_amount)}</span>
                          </div>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="py-0 pb-4">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Invoice No</TableHead>
                              <TableHead>Billing Month</TableHead>
                              <TableHead className="text-right">Amount</TableHead>
                              <TableHead>Currency</TableHead>
                              <TableHead className="text-right">Est. INR</TableHead>
                              <TableHead className="text-right">Days Outstanding</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {group.invoices.map((inv) => (
                              <TableRow key={inv.invoice_no}>
                                <TableCell className="font-mono">{inv.invoice_no}</TableCell>
                                <TableCell>{inv.billing_month}</TableCell>
                                <TableCell className="text-right font-mono">
                                  {formatCurrency(inv.bill_amount, inv.bill_currency)}
                                </TableCell>
                                <TableCell>{inv.bill_currency}</TableCell>
                                <TableCell className="text-right font-mono text-primary">
                                  {formatCurrency(inv.estimated_revenue_inr || inv.bill_amount)}
                                </TableCell>
                                <TableCell className="text-right font-mono text-destructive">
                                  {Math.round(inv.days_outstanding)} days
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-4" />
                  No unpaid invoices
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Report 5: Currency Revenue */}
        <TabsContent value="currency">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Currency-Wise Revenue</CardTitle>
              <Button variant="outline" size="sm" onClick={() => exportReport(currencyData, 'currency_revenue')}>
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </CardHeader>
            <CardContent>
              {renderFilters()}
              {loading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : currencyData.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={currencyData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          dataKey="total_paid_inr"
                          nameKey="currency"
                          label={({ currency, percent }) => `${currency} (${(percent * 100).toFixed(0)}%)`}
                        >
                          {currencyData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Currency</TableHead>
                          <TableHead className="text-right">Bill Amount</TableHead>
                          <TableHead className="text-right">Paid (INR)</TableHead>
                          <TableHead className="text-right">Invoices</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {currencyData.map((item, idx) => (
                          <TableRow key={item.currency}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                                {item.currency}
                              </div>
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(item.total_bill_amount, item.currency)}
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(item.total_paid_inr)}
                            </TableCell>
                            <TableCell className="text-right">{item.count}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">No data available</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Report 6: Bank Inflow */}
        <TabsContent value="bank">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Bank-Wise Inflow</CardTitle>
              <Button variant="outline" size="sm" onClick={() => exportReport(bankData, 'bank_inflow')}>
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </CardHeader>
            <CardContent>
              {renderFilters()}
              {loading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : bankData.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={bankData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                        <XAxis type="number" tickFormatter={formatNumber} />
                        <YAxis type="category" dataKey="bank" width={80} />
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Bar dataKey="total_inflow" fill="#2A9D8F" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Bank</TableHead>
                          <TableHead className="text-right">Total Inflow (INR)</TableHead>
                          <TableHead className="text-right">Payments</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {bankData.map((item) => (
                          <TableRow key={item.bank}>
                            <TableCell className="font-medium">{item.bank}</TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(item.total_inflow)}
                            </TableCell>
                            <TableCell className="text-right">{item.payment_count}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">No data available</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
